import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ActionMenu, GenericTable, tCell, Button, DetailCard, generateDetailRows, toastMessage } from "../utils";
import theme, { buttonClasses } from "../utils/theme";
import { useUserDetail, useUserPermissions, useUsers, useOrgs, usePlant, useRoles } from "../../services/hooks";
import { capitalizeCamelCase, isObjectEqual } from "../../services/functions";
import { Navigate } from "react-router-dom/dist";
import { useGetUserDetailMutation, useToggleUserStatusMutation } from "../../api/userSlice";
import Genericfilter from "../utils/Genericfilter";
import { debounce } from "../../services/functions";
import { GenericstatsV2 } from "../utils/Genericstats";
import CommonTour, { TourIcon } from "../Tour/CommonTour";
import { Tooltip } from "@mui/material";
const userTourDummyData = [
	{
		addedBy: "665ef33146bdb82ebce76709",
		addedOn: "2025-04-23T05:21:12.028Z",
		email: "matt_murdock@yopmail.com",
		mobile: "9000000000",
		moduleTour: {
			dashboard: false,
			analytics: false,
			devices: false,
			tags: false,
			users: false,
			approvalManagement: false,
			checkpoint: false,
			productCategories: false,
			products: false,
			suppliers: false,
		},
		name: "Matthew Murdock",
		orgId: "665ef2d646bdb82ebce7669a",
		orgName: "LexCorp",
		password: "$2b$10$hpppJl5zKpU9wCCLPTwCVOYHDeAFQJSpy00bAUMhp4sxP30pHzTfy",
		receivedGuidedTour: false,
		status: "ACTIVE",
		userRole: "BAY OPERATOR",
		userRoleId: "6660519558db07da1e428354",
		userType: "user",
		__v: 0,
		_id: "680878c77f836b33a69db09b",
	},
];
const UserManagementTour = [
	{
		target: ".title",
		content: "Welcome to User Management! Here you can manage all user accounts and permissions.",
		placement: "bottom",
	},
	{
		target: ".genericStats-containter",
		content: "These cards provide an overview of user statistics at a glance.",
		placement: "bottom",
	},
	{
		target: "#Genericfilter",
		content: "Use these filters to search for users by name, email, mobile number, or organization.",
		placement: "bottom",
	},
	{
		target: ".a_tag_no",
		content: "Click here to create new user accounts.",
		placement: "left",
		condition: () => !!document.querySelector('[aria-label="Add User"]'),
	},
	{
		target: ".main_content",
		content: "This table lists all users with their key information. Click on names to view details.",
		placement: "top",
	},
	{
		target: ".userName", // Target clickable user names
		content: "Click any user name to view their detailed information.",
		placement: "right",
		action: () => {
			const firstUserName = document.querySelector(".userName");
			if (firstUserName) {
				firstUserName.click();
			}
		},
	},
	{
		target: ".detail_card",
		content: "This panel shows comprehensive user details including contact information and role.",
		placement: "left",
		condition: () => !!document.querySelector(".detail_card"),
	},
	{
		target: ".actionMenu",
		content: "Use these action buttons to edit user details or manage permissions.",
		placement: "left",
	},
];
const initUserFilter = {
	pagesize: 10,
	page_no: 1,
};

function getUserDetails(data) {
	const dataToShow = ["name", "email", "mobile", "orgName", "userRole", "status"];
	const res = dataToShow.map((key) => ({ label: capitalizeCamelCase(key), value: data[key] || "-" }));
	return res;
}
const UserManagement = () => {
	const { userPermissions, isFetched } = useUserPermissions();
	const [toggleUserStatus]= useToggleUserStatusMutation()
	const [fetchUserDetail] = useGetUserDetailMutation();
	const [fetchStats, setFetchStats] = useState(false);
	const { userDetail, isSuperAdmin } = useUserDetail();
	const navigate = useNavigate();
	const location = useLocation();
	const { filterData } = location.state || {};
	const filterRef = useRef(initUserFilter);
	if (filterData) filterRef.current = { ...filterRef.current, ...filterData };
	const shouldHandlePlant = isSuperAdmin || userPermissions?.managePlant?.value;
	const tourRef = useRef();
	const [isTourMode, setIsTourMode] = useState(false);
	const shouldRunTour = !userDetail?.moduleTour?.users;
	const updateDateRef = useRef(null);
	
	//  make into lazy query
	const { orgOptions } = useOrgs();
	const { plants, plantOptions, getPlantDetails, clearPlants } = usePlant({}, "userPlants");
	const { roleOptions, getRoles, clearRole} = useRoles({});
	useEffect(() => {
		if (userPermissions?.managePlant?.value && !isSuperAdmin) {
			getPlantDetails({});
		}
	}, [userDetail]);

	// Using this useEffect to resolve the issue of page refresh state.
	// The problem was: when we refreshed the page and currentFilters existed, the filters were not being cleared.
	useEffect(() => {
		navigate(".", { replace: true });
	}, [navigate]);

	useEffect(() => {
		if (filterData?.orgId && isSuperAdmin) {
			getPlantDetails({ orgId: filterData.orgId });
			getRoles({orgId:filterData.orgId})
		}
	}, [filterData]);
	let filterFields = [
		{
			inputType: "textfield",
			id: "2",
			name: "name",
			type: "text",
			label: "Name",
			placeholder: "Name",
			readOnly: false,
			error: "",
			value: filterRef.current?.name || "",
		},
		{
			inputType: "textfield",
			id: "3",
			name: "email",
			type: "text",
			label: "Email",
			placeholder: "Email",
			readOnly: false,
			error: "",
			value: filterRef.current?.email || "",
		},
		{
			inputType: "textfield",
			id: "4",
			name: "mobile",
			type: "text",
			label: "Mobile number",
			placeholder: "Mobile number",
			readOnly: false,
			error: "",
			value: filterRef.current?.mobile || "",
		},
	];

	if (isSuperAdmin) {
		filterFields.push({
			inputType: "dropdown",
			id: "orgId",
			name: "orgId",
			label: "Organization",
			error: "",
			option: orgOptions,
			value: filterRef.current?.orgId || "",
		});
	}
	if (shouldHandlePlant) {
		filterFields.push({
			inputType: "dropdown",
			id: "plantIds",
			name: "plantIds",
			label: "Warehouse",
			error: "",
			option: plantOptions,
			value: filterRef.current?.plantIds,
		});
	}
	if(userPermissions?.managePlant?.value && userPermissions?.getUserRoles?.value || isSuperAdmin ){
		filterFields.push({
			inputType: "dropdown",
			id: "userRole",
			name: "userRole",
			type: "text",
			label: "User Role",
			placeholder: "User Role",
			option:roleOptions,
			error: "",
			value: filterRef.current?.userRole,
		})
	}
	// console.log('userdetail', useUserDetail());
	// console.log('userpermission', useUserPermissions());

	// const filterRef = useRef(initUserFilter);
	const { page_no, pagesize } = filterRef.current;
	const { users, count, isLoading, getUsers } = useUsers(filterRef.current);

	const [isOpen, setIsOpen] = useState(false);
	const [detail, setDetail] = useState(null);
	const handleDetailClose = () => {
		setIsOpen(false);
		setDetail(null);
	};

	const handleDeviceView = (e, index) => {
		// console.log(index, "view", devices[index]);
		// setDetail(generateDetailRows(getUserDetails(users[index] || userTourDummyData)));
		setDetail(generateDetailRows(getUserDetails(shouldRunTour || isTourMode ? userTourDummyData[0] : users[index])));
		setIsOpen(true);
	};
	const fetchToggleOrgStatus = async (i, isApproved) => {
		// console.log("ID", i)
		const payload = {
			ids: [users[i]._id],
			isApproved: isApproved,
			remark: "SuperAdmin Disabled",
		};
		// console.log("Payload", payload)
		try {
			const response = await toggleUserStatus(payload).unwrap();
			// console.log("response", response);
			if (response?.error) {
				toastMessage(false, response?.usersToggleResult?.message);
			} else {
				toastMessage(true, response?.usersToggleResult?.message);
			}
			getUsers({})
		} catch (error) {
			console.error("Error toggling organization status:", error);
		}
	};
	let actionHandlers = {};
	if (userPermissions?.updateUser?.value) {
		actionHandlers.edit = async (id) => {
			// console.log(id, "edit");
			const { _id } = users[id];

			const {
				data: { data, error, message },
			} = await fetchUserDetail({ userId: _id });
			if (error) {
				toastMessage(error, message);
			} else {
				updateDateRef.current = data;
			}
			const updateData = updateDateRef.current;
			navigate("/users/edit-user-form", { state: { updateData: updateData, filterData: filterRef.current } });
		};
	}
	if (userPermissions?.addUser?.value) {
		actionHandlers.duplicate = async (id) => {
			const { _id } = users[id];

			const {
				data: { data, error, message },
			} = await fetchUserDetail({ userId: _id });
			if (error) {
				toastMessage(error, message);
			} else {
				updateDateRef.current = data;
			}
			const updateData = updateDateRef.current;
			navigate("/users/add-user-form", { state: { updateData: updateData, filterData: filterRef.current } });
		};
	}
	// console.log(updateDateRef);
	if (userPermissions?.deleteUser?.value) {
		// actionHandlers.delete = async (id) => {
		// 	// console.log(id, "delete");
		// };
	}

	// Creating a map for plantId to plantName 
	const plantMap = React.useMemo(() => {
		const map = {};
		if (plants && plants.length > 0) {
			plants.forEach(plant => {
				map[plant._id] = plant.plantName;
			});
		}
		return map;
	}, [plants]);

	// Function to get plant names from plant IDs
	const getPlantNamesWithTooltip = (plantIds) => {
		if (!plantIds || !Array.isArray(plantIds) || plantIds.length === 0) {
			return { displayText: "-", allPlantNames: [] };
		}
		
		const allPlantNames = plantIds
			.map(plantId => plantMap[plantId])
			.filter(name => name);

		if (allPlantNames.length === 0) {
			return { displayText: "-", allPlantNames: [] };
		}

		if (allPlantNames.length <= 2) {
			return { 
				displayText: allPlantNames.join(", "), 
				allPlantNames: [] 
			};
		}

		// Show first 2 plant names and indicate there are more
		const displayText = `${allPlantNames.slice(0, 2).join(", ")} +${allPlantNames.length - 2} more`;
		return { displayText, allPlantNames };
	};


	const renderPlantNames = (plantIds) => {
		const { displayText, allPlantNames } = getPlantNamesWithTooltip(plantIds);	
		if (allPlantNames.length === 0) {
			return tCell(displayText);
		}

		const plantCell = (
			<Tooltip
				title={
					<div>All Warehouses:<br />{allPlantNames.join(', ')}</div>
				}
			>
				<span style={{ cursor: 'pointer', textDecoration: 'underline dotted', color: theme.themeOrange}}>
					{displayText}
				</span>
			</Tooltip>
		);
		return tCell(plantCell);
	};

	let header = ["S.no", "Customer Name", "Email", "User Role", "Status", ];
	if (isSuperAdmin) {
		header.splice(2, 0, "Org Name");
	}
	if (userPermissions?.managePlant?.value && plants?.length > 1) {
		header.push("Warehouse")
	}
	if (userPermissions?.updateUser?.value || userPermissions?.deleteUser?.value) {
		header.push("Action");
	}

	const rows =
		(shouldRunTour || isTourMode ? userTourDummyData : users)?.map((d, i) => {
			let row = [];
			const rowActionHandlers = { ...actionHandlers }; 
			if (userDetail?.userType === "superAdmin") {
			const isActive = d?.status === "ACTIVE";
			rowActionHandlers[isActive ? "Disable" : "Enable"] = () =>
				fetchToggleOrgStatus(i, !isActive);
			}
			row.push(tCell((page_no - 1) * pagesize + i + 1), tCell(d.name, theme.themeOrange, "pointer", handleDeviceView, i, "userName"), tCell(d.email), tCell(d.userRole), tCell(d.status));
			if (isSuperAdmin) {
				row.splice(2, 0, tCell(d.orgName));
			}
			if (userPermissions?.managePlant?.value && plants?.length > 1) {
				const plantCell = renderPlantNames(d.plantIds);
				row.push(plantCell);
			}
			if (userPermissions?.updateUser?.value || userPermissions?.deleteUser?.value) {
				row.push(<ActionMenu id={i} handlers={rowActionHandlers} />);
			}
			return row;
		}) || [];

	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getUsers(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value };
		getUsers(filterRef.current);
	};
	const onUpdateSuccess = () => {
		getUsers(filterRef.current);
		setFetchStats((old) => !old);
	};
	/**
	 * We use GenericFilter component for all CRUD filters. That component maintains its own state for the filter
	 * and that object is sent here to the handleFilterChange function.
	 * Mutating this _filter object will cause the filter state to change on rerender (It's probably because of object reference in JS. The filter object from the state gets mutated and then the component gets re-rendered  due to any reason and now the state will have the mutated object)
	 * Here we have relationship between Org and Plant. On selecting an org, we will fetch its plant. So in order to clear previous selected plant when selecting different org, we will mutate the _filter object
	 */
	const handleFilterChange = (_filter) => {
		let filter = { ..._filter };

		for (let key in filter) {
			if (!filter[key]) delete filter[key];
		}
		if (isSuperAdmin) {
			if (filter.orgId) {
				// filter.orgId = filter.orgId?.value;

				if (shouldHandlePlant && filter.orgId != filterRef.current?.orgId) {
					// when switching organization, we want to
					// - get plants for that org
					// - clear plantIds from the local filter object
					// - clear plantIds from original filter object, mutating the state and clearing the plant dropdown
					delete filter.plantIds;
					delete _filter.plantIds;
					getPlantDetails({ orgId: filter.orgId });
					getRoles({orgId: filter.orgId})
				}
			} else {
				delete filter.plantIds;
				delete filter.userRole
				clearPlants();
				clearRole();
			}
		}
		// if (filter.plantId) filter.plantId = filter.plantId?.value;

		filter.page_no = 1;
		filter.pagesize = 10;

		if (!isObjectEqual(filterRef.current, filter)) {
			filterRef.current = { ...filter };
			getUsers(filterRef.current);
		}
	};
	const handleTourStart = () => {
		setIsTourMode(true);
		tourRef.current?.restartTour();
	};

	if (isFetched && userPermissions && Object.keys(userPermissions).length && !userPermissions.getUsers?.value) return <Navigate to="/not-authorized" replace />;
	const handleAddUser = () => {
		navigate("/users/add-user-form", { state: { filterData: filterRef.current } });
	};
	return (
		<div className="contentpanel">
			<CommonTour
				ref={tourRef}
				steps={UserManagementTour}
				moduleName="users"
				onTourComplete={() => {
					setIsTourMode(false);
					handleDetailClose();
				}}
			/>
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">
						User Management
						<span title="Replay Tour" onClick={handleTourStart} style={{ cursor: "pointer" }}>
							<TourIcon />
						</span>
					</h1>
				</div>
				{userPermissions?.addUser?.value ? (
					<div className="cust-col-5 flex-jc-end">
						<div>
							<Button text="Add User" style={buttonClasses.lynkitOrangeEmpty} onClick={handleAddUser} />
						</div>
					</div>
				) : null}
			</div>
			<hr className="bgg" />
			<GenericstatsV2 statsFor={"users"} fetchStats={fetchStats} />
			<div className="cust-row flex-algn-cent">
				<Genericfilter filterFields={filterFields} onFilterChange={debounce((filter) => handleFilterChange(filter), 500)} addOnClear />
			</div>

			<div className="main_content">
				<GenericTable
					header={header}
					rows={rows}
					pageCount={count}
					pageNo={filterRef.current.page_no}
					limit={filterRef.current.pagesize}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					isLoading={isLoading}
				/>
				<DetailCard isOpen={isOpen} onClose={handleDetailClose} title={"User Details"}>
					{detail}
				</DetailCard>
			</div>
		</div>
	);
};

export default UserManagement;
